import type { Rasa } from '@iqrapath/shared'
import { useQuery } from '@tanstack/react-query'
import type { ObjId } from '../../components/Obj.tsx'
import { api } from '../../lib/api.ts'

/** Mirrors apps/api/src/services/pegangan.ts. */
export type PeganganItem = { id: string; rasa: Rasa; judul: string; ringkas: string; ref: string; reviewed: boolean }
export type PassageVerse = { k: number; ar: string; latin: string | null; terjemah: string; audio: string | null }
/** `verses` null = the Qur'an dataset is not installed: show the reference only. `url` = the source page (Kemenag). */
export type Passage = { ref: string; url: string; verses: PassageVerse[] | null }
export type PeganganCard = {
  id: string
  rasa: Rasa
  judul: string
  ringkas: string
  ayat: Passage
  makna: string
  temanmu: string
  teladan: { teks: string; ref: string; url: string } | null
  /** Sahih hadith from HadeethEnc, verbatim (text, grade, attribution) with a link to the source */
  hadis: { id: string; text: string; ar: string | null; attribution: string; grade: string; url: string; source: string }[]
  langkah: { sekarang: string; hariIni: string; mingguIni: string }
  doa: Passage | null
  catatan: string | null
  /** false = draft, not yet reviewed by an ustadz (the app says so) */
  reviewed: boolean
  /** drafted with AI */
  ai: boolean
}

/** Label, object and soft gradient per feeling (docs/UX-PLAYBOOK.md §7.4: "gradien per kartu"). */
export const RASA_INFO: Record<Rasa, { label: string; obj: ObjId; tint: [string, string] }> = {
  kesal: { label: 'Kesal', obj: 'rasa-kesal', tint: ['#F8E6DA', '#EDC9B2'] },
  cemas: { label: 'Cemas', obj: 'rasa-cemas', tint: ['#E4F2EE', '#C3E1D8'] },
  sedih: { label: 'Sedih', obj: 'rasa-sedih', tint: ['#E4E9F5', '#C7D1EA'] },
  capek: { label: 'Capek', obj: 'rasa-capek', tint: ['#F7EEDA', '#E9D5A9'] },
  gelisah: { label: 'Gelisah', obj: 'rasa-gelisah', tint: ['#ECE6F4', '#D3C8E7'] },
  jauh: { label: 'Jauh dari Allah', obj: 'rasa-jauh', tint: ['#DFEAEC', '#BCD3D8'] },
  syukur: { label: 'Bersyukur', obj: 'rasa-syukur', tint: ['#F8F1D8', '#EEDB9C'] },
}

export const tintOf = (r: Rasa) => `linear-gradient(135deg, ${RASA_INFO[r].tint[0]}, ${RASA_INFO[r].tint[1]})`

const TEN_MIN = 10 * 60_000
export const usePeganganList = (rasa?: Rasa) =>
  useQuery({
    queryKey: ['pegangan', 'list', rasa ?? 'semua'],
    queryFn: () => api<{ ok: true; items: PeganganItem[]; today: string | null }>('GET', `/api/pegangan${rasa ? `?rasa=${rasa}` : ''}`),
    staleTime: TEN_MIN,
  })

export const usePegangan = (id: string) =>
  useQuery({
    queryKey: ['pegangan', 'card', id],
    queryFn: async () => (await api<{ ok: true; card: PeganganCard }>('GET', `/api/pegangan/${id}`)).card,
    staleTime: TEN_MIN,
  })
